import { type RefObject, useEffect, useState } from 'react';

/** Where a sideways scrolling element stands. */
export interface HorizontalOverflow {
    /** Whether its content is wider than it is. */
    overflows: boolean;
    /** Whether it shows its left edge, so there is nothing to scroll back to. */
    atStart: boolean;
    /** Whether it shows its right edge. */
    atEnd: boolean;
}

const FITS: HorizontalOverflow = { overflows: false, atStart: true, atEnd: true };

// Chrome reports fractional scroll positions and rounds scrollWidth, so a table that fits
// can measure a pixel wider than its box, and a scroller at its end a pixel short of it.
const TOLERANCE_PX = 1;

export function measureHorizontalOverflow(element: HTMLElement): HorizontalOverflow {
    const range = element.scrollWidth - element.clientWidth;
    if (range <= TOLERANCE_PX) return FITS;
    return {
        overflows: true,
        atStart: element.scrollLeft <= TOLERANCE_PX,
        atEnd: element.scrollLeft >= range - TOLERANCE_PX,
    };
}

function sameOverflow(a: HorizontalOverflow, b: HorizontalOverflow): boolean {
    return a.overflows === b.overflows && a.atStart === b.atStart && a.atEnd === b.atEnd;
}

/**
 * Whether `ref`'s element overflows sideways and which of its ends it shows, kept current
 * as it scrolls and as it or its content changes size: a narrower page, another language
 * or another set of pens can each make the table fit or overflow.
 */
export function useHorizontalOverflow(ref: RefObject<HTMLElement | null>): HorizontalOverflow {
    const [overflow, setOverflow] = useState(FITS);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const update = () => {
            const next = measureHorizontalOverflow(element);
            // Every scroll event lands here, so an unchanged state keeps its identity and
            // does not re-render the buttons.
            setOverflow((current) => (sameOverflow(current, next) ? current : next));
        };
        update();
        element.addEventListener('scroll', update, { passive: true });
        // jsdom has no ResizeObserver; the measurement above still runs there.
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        observer?.observe(element);
        // The content grows without the box changing when a column or a pen is added. It is
        // the table, which overflows the full-width wrapper it sits in.
        const content = element.querySelector('table') ?? element.firstElementChild;
        if (content) observer?.observe(content);
        return () => {
            element.removeEventListener('scroll', update);
            observer?.disconnect();
        };
    }, [ref]);

    return overflow;
}
